import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { IUserExistsCheck } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, Keyboard, Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { Button, ButtonSize } from '../components';
import { IAppStore } from '../HomeWorkouts';
import Theme from '../theme';
import { FormError, TextInput } from '../ui';
import { createUnauthenticatedUserServiceClient } from '../utils/apiClientFactory';
import { defaultScale } from '../utils/scaling';
import {
  isRequired,
  minLength6,
} from '../utils/validator';
import {
  preAccountSetPasswordFormSubmitted as preAccountSetPasswordFormSubmittedAction,
  preAccountSetPasswordScreenMounted as preAccountSetPasswordScreenMountedAction,
} from './actions';
import { TermsAndConditions } from './components';
import { styles as signupFormStyles } from './SignupFormsStyles';

interface IProps {
  preAccountSetPasswordLoading: boolean;
  existingUserDate: IUserExistsCheck;
  wholeScreenLoading: boolean;
  preAccountSetPasswordFormSubmitted: (
    password: string,
    navigate: (route: string) => void,
  ) => void;
  preAccountSetPasswordScreenMounted: (
    navigate: (route: string) => void,
  ) => void;
  error: string;
  t: (key: string, options?: any) => string;
  navigation: {
    navigate: (route: string) => void;
    replace: (route: string) => void;
  };
  handleSubmit: (fn: (values: ISetPassword) => void) => () => any;
}

interface ISetPassword {
  password: string;
}

interface IState {
  termsAccepted: boolean;
}

class PreAccountSetPassword extends PureComponent<IProps, IState> {
  private readonly unAuthenticatedUserServiceClient: UserServiceClient;

  constructor(props) {
    super(props);
    this.unAuthenticatedUserServiceClient = createUnauthenticatedUserServiceClient();
    this.state = {
      termsAccepted: false,
    };
  }

  public componentDidMount() {
    const { preAccountSetPasswordScreenMounted, navigation } = this.props;
    const routingFunction = Platform.OS === 'web' ? navigation.navigate : navigation.replace;
    preAccountSetPasswordScreenMounted(routingFunction);
  }

  public render() {
    const {
      t,
      handleSubmit,
      preAccountSetPasswordLoading,
      error,
      existingUserDate,
      wholeScreenLoading,
    } = this.props;
    const { termsAccepted } = this.state;

    if (wholeScreenLoading) {
      return (
        <View style={[signupFormStyles.container, { justifyContent: 'center' }]}>
          <ActivityIndicator
            size={'small'}
            color={Theme.PRIMARY}
            animating
          />
        </View>
      );
    }

    return (
      <View style={signupFormStyles.container}>
        <ScrollView
          scrollEnabled={false}
          keyboardShouldPersistTaps='handled'>

          <Text style={styles.heading}>
            {existingUserDate?.firstname
              ? t('auth:preAccountSetPasswordHeading', { name: existingUserDate.firstname })
              : t('auth:setYourPassword')
            }
          </Text>

          <Field
            forwardRef
            name='password'
            autoFocus
            secureTextEntry
            returnKeyType='done'
            autoCapitalize='none'
            component={TextInput}
            placeholder={t('formFields:newPassword')}
            validate={[isRequired, minLength6]}
            onSubmitEditing={handleSubmit(this.onSubmit)}
          />

          <View style={styles.termsContainer}>
            <TermsAndConditions
              canAccept={true}
              isMinor={false}
              textStyle={signupFormStyles.tandcText}
              preAgreementText={t('byCreatingYouAgreeToTandC')}
              acceptTermsAndConditions={this.acceptTermsAndConditions}
              termsAccepted={termsAccepted}
              checkBoxSize={defaultScale(20)}
            />
          </View>

          <FormError error={error} />

          <Button
            fontColor={Theme.WHITE}
            size={ButtonSize.MEDIUM}
            isLoading={preAccountSetPasswordLoading}
            containerStyle={{ marginTop: defaultScale(10) }}
            onPress={handleSubmit(this.onSubmit)}
            title={t('auth:preAccountSetPasswordSubmitButton')}
          />
        </ScrollView>
      </View>
    );
  }

  private acceptTermsAndConditions = () => {
    this.setState({ termsAccepted: !this.state.termsAccepted });
    Keyboard.dismiss();
  }

  private onSubmit = async (values: ISetPassword) => {
    const { preAccountSetPasswordFormSubmitted, navigation, t } = this.props;
    const { termsAccepted } = this.state;
    if (!termsAccepted) {
      throw new SubmissionError({ _error: new Error(t('auth:pleaseAcceptTerms')) });
    }
    preAccountSetPasswordFormSubmitted(values.password, navigation.navigate);
  }

}

const styles = StyleSheet.create({
  heading: {
    ...Theme.textStyle.heading,
    marginBottom: defaultScale(20),
  },
  termsContainer: {
    marginVertical: defaultScale(10),
  },
});

const mapDispatchToProps = (dispatch) => {
  return {
    preAccountSetPasswordFormSubmitted: (password, navigate) =>
      dispatch(preAccountSetPasswordFormSubmittedAction(password, navigate)),
    preAccountSetPasswordScreenMounted: (replaceRoute) =>
      dispatch(preAccountSetPasswordScreenMountedAction(replaceRoute)),
  };
};

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    preAccountSetPasswordLoading: state.auth.signup.preAccountSetPasswordLoading,
    existingUserDate: state.auth.signup.userExistsCheckData,
    wholeScreenLoading: state.auth.signup.preAccountSetPasswordScreenLoading,
  };
};

export const PreAccountSetPasswordScreen = translate(['auth', 'formFields'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(
    reduxForm({ form: 'CaptureEmail' })(PreAccountSetPassword),
  ),
);
